import { useState } from 'react';
import { findFirstPartyMcpForConnection } from '@wix/ai-scheduling-assistant-constants';
import { ChevronRight } from 'lucide-react';
import { mcpStatusLabel, type McpConnectionSummary } from '../../../../lib/mcp';
import { CONNECTORS_PAGE, connectorRowSlugs, mcpSectionShowsConnection, type AppsState, type McpController } from '../../../../lib/use-connectors';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useTranslation } from '../../../../lib/i18n';
import { LogoFallback } from '../ConnectorLogo';
import { CardStack } from './CardStack';
import { Button, Card } from '../../ui';
import { Modal } from '../../../Modal';
import { PlusIcon } from '../icons';
import styles from './mobile.module.css';

type Translate = (k: string, o?: Record<string, unknown>) => string;

function mcpToolsLabel(connection: McpConnectionSummary, t: Translate): string | undefined {
  if (connection.toolCount === undefined) return undefined;
  const unit = connection.toolCount === 1 ? t('connectors.mcp.toolOne') : t('connectors.mcp.toolOther');
  return `${connection.toolCount} ${unit}`;
}

/**
 * "MCP Servers · N" (Figma 13131-78922): the owned MCP connections as tappable rows + a full-width
 * "Connect an MCP server" button. Shares the desktop `useMcpController` verbatim; the connect flow is
 * a chat handoff (the assistant runs ManageMcp), passed in as `connectCta`.
 */
export function McpServersMobile({
  appsState,
  connectCta,
  controller,
}: {
  appsState: AppsState;
  connectCta: { route: string; path: string };
  controller: McpController;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { state, busy, actionError, remove, logConnectClick } = controller;
  const [detail, setDetail] = useState<McpConnectionSummary | null>(null);

  const rowedSlugs = connectorRowSlugs(appsState);
  const serverFallback: string = t('connectors.mcp.serverFallback');
  const owned =
    state.kind === 'ready' ? state.connections.filter((c) => mcpSectionShowsConnection(findFirstPartyMcpForConnection(c), rowedSlugs)) : [];

  // Each owned server as a card, aggregated into the same deck the Connected tools use.
  const items = owned.map((connection) => {
    const name = connection.name || connection.url || serverFallback;
    const tools = mcpToolsLabel(connection, t);
    return {
      key: connection.id,
      node: (
        <Card
          component="button"
          interactive
          className={`${styles.mcpRow} ${styles.cardShadow}`}
          onClick={() => {
            logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'open_mcp' });
            setDetail(connection);
          }}
        >
          <LogoFallback letter={name.charAt(0)} size={40} />
          <div className={styles.rowText}>
            <div className={styles.rowName}>{name}</div>
            {tools && <div className={styles.rowDesc}>{tools}</div>}
          </div>
          <ChevronRight size={20} aria-hidden className={styles.rowChevron} />
        </Card>
      ),
    };
  });

  return (
    <section>
      {/* With servers, the deck owns the "MCP Servers · N" head + its View All toggle; with none
          (loading / empty) there is nothing to aggregate, so just the head renders. */}
      {items.length > 0 ? (
        <CardStack title={t('connectors.mcp.title')} count={owned.length} biToggle="mcp" items={items} />
      ) : (
        <h3 className={styles.sectionTitle}>
          {t('connectors.mcp.title')}
          {state.kind === 'ready' && <span className={styles.sectionCount}>{` · ${owned.length}`}</span>}
        </h3>
      )}

      {state.kind === 'error' && (
        <div role="alert" className={styles.errorLine}>
          {t('connections.error')}
        </div>
      )}
      {actionError && (
        <div role="alert" className={styles.errorLine}>
          {actionError}
        </div>
      )}

      <div className={styles.mcpConnectWrap}>
        <Button href={connectCta.route} linkAs={connectCta.path} size="large" fullWidth startIcon={<PlusIcon size={16} />} onClick={logConnectClick}>
          {t('connectors.mcp.connect')}
        </Button>
      </div>

      {detail && (
        <McpDetailModal
          connection={detail}
          busy={busy}
          onRemove={(id, name) => {
            remove(id, name);
            setDetail(null);
          }}
          onClose={() => setDetail(null)}
        />
      )}
    </section>
  );
}

function McpDetailModal({
  connection,
  busy,
  onRemove,
  onClose,
}: {
  connection: McpConnectionSummary;
  busy: ReadonlySet<string>;
  onRemove: (id: string, name: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const name = connection.name || connection.url || t('connectors.mcp.serverFallback') || '';
  const { label, healthy } = mcpStatusLabel(connection.status, connection.lastError);
  const tools = mcpToolsLabel(connection, t);
  const isBusy = busy.has(connection.id);
  return (
    <Modal open onClose={onClose} width={420} maxHeightPx={650} scrollBody labelledBy="mcp-mobile-detail-title">
      <div className={styles.modalHead}>
        <LogoFallback letter={name.charAt(0)} size={40} />
        <div className={styles.rowText}>
          <div id="mcp-mobile-detail-title" className={styles.rowName}>
            {name}
          </div>
          <div className={styles.mcpStatus}>
            <span
              aria-hidden
              className={styles.mcpDot}
              style={{ background: healthy ? 'var(--accent-solid)' : 'rgba(var(--ds-fill-destructive-hover-rgb), 0.85)' }}
            />
            {label}
            {tools ? ` · ${tools}` : ''}
          </div>
        </div>
      </div>
      {connection.url && <div className={styles.modalUrl}>{connection.url}</div>}
      <Button
        variant="text"
        color="destructive"
        fullWidth
        onClick={() => onRemove(connection.id, name)}
        disabled={isBusy}
        loading={isBusy}
        aria-label={t('connectors.mcp.removeNamed', { name })}
      >
        {t('common.disconnect')}
      </Button>
    </Modal>
  );
}
